'use strict'

/**
 * マトリクス tbody の 1 行。React.memo で包み、props（row / spans / columns / statusByCode / mappingLoading / onGraphClick / spacer
 * 幅）が変わらない限り再レンダーしない。
 *
 * 2D 仮想スクロール対応: 描画するセルは「表示領域内の列ウィンドウ（columns）」のみで、画面外の列は左右の spacer セルで幅だけ確保する。majorSpan/middleSpan/dashboardSpan
 * は行ウィンドウに合わせて丸めた値を親から受け取る。 行の参照可否・未設定件数・各セルの状態は事前計算済み statusByCode を O(1) 参照する。 高さ実測のため tr に data-matrix-row を付与する。
 */

import React from 'react'
import PropTypes from 'prop-types'
import CellDot from './CellDot'
import ReferableIcon from './ReferableIcon'
import { isRowReferableFromStatus } from '../extra/helpers'
import { COLUMN_GROUP_BORDER_COLOR } from '../extra/constants'

/**
 * @param {{
 *   row: object
 *   majorSpan: number
 *   middleSpan: number
 *   dashboardSpan: number
 *   columns: object[]
 *   statusByCode: { [code: string]: 'set' | 'unset' | 'remove' }
 *   mappingLoading: boolean
 *   onGraphClick: (row: object) => void
 *   leftSpacerWidth: number
 *   rightSpacerWidth: number
 * }} props
 * @returns {JSX.Element}
 */
function MatrixRow({
  row,
  majorSpan,
  middleSpan,
  dashboardSpan,
  columns,
  statusByCode,
  mappingLoading,
  onGraphClick,
  leftSpacerWidth,
  rightSpacerWidth
}) {
  const required = row.required_columns || []
  const optional = row.optional_columns || []
  // 列ループ内で参照するため Set 化（includes の O(n) を避ける）
  const requiredSet = new Set(required)
  const optionalSet = new Set(optional)
  const referable = isRowReferableFromStatus(row, statusByCode)
  // 未設定件数は 必須 のみ数える（任意 は参照可否に影響しない）
  const notConfiguredCount = required.filter((code) => {
    const status = statusByCode[code]
    return status === 'unset' || status === 'remove'
  }).length
  const dashboardLabel = row.dashboardCode ? `${row.dashboardCode} ${row.dashboardName}`.trim() : row.dashboardName

  return (
    <tr data-matrix-row="1">
      {majorSpan > 0 && (
        <td
          rowSpan={majorSpan}
          className="p-1.5 sticky left-0 z-10 text-left text-[10px] font-semibold sticky-body-cell matrix-table-leftmost matrix-table-col-140"
          style={{ verticalAlign: 'top', overflow: 'hidden' }}
        >
          <span className="block truncate min-w-0" title={row.majorLabel}>
            {row.majorLabel}
          </span>
        </td>
      )}
      {middleSpan > 0 && (
        <td
          rowSpan={middleSpan}
          className="p-1.5 sticky left-[140px] z-10 text-left text-[10px] font-semibold sticky-body-cell matrix-table-col-140"
          style={{ verticalAlign: 'top', overflow: 'hidden' }}
        >
          <span className="block truncate min-w-0" title={row.middleName}>
            {row.middleName}
          </span>
        </td>
      )}
      {dashboardSpan > 0 && (
        <td
          rowSpan={dashboardSpan}
          className="p-1.5 sticky left-[280px] z-10 text-left text-[10px] font-semibold sticky-body-cell matrix-table-col-180"
          style={{ verticalAlign: 'top', overflow: 'hidden' }}
        >
          <span className="block truncate min-w-0" title={dashboardLabel}>
            {dashboardLabel}
          </span>
        </td>
      )}
      <td
        className="p-1.5 sticky left-[460px] z-10 sticky-body-cell matrix-graph-name-cell matrix-table-col-200"
        style={{ overflow: 'hidden' }}
      >
        <button
          type="button"
          className="block w-full text-left text-[10px] font-semibold text-gray-900 truncate min-w-0"
          title={row.graphName}
          onClick={() => onGraphClick(row)}
        >
          {row.graphName}
        </button>
      </td>
      <td className="p-1 sticky left-[660px] z-10 text-center sticky-body-cell matrix-table-referable-cell matrix-table-col-80">
        {mappingLoading ? (
          <span className="matrix-referable-loading" title="読み込み中" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        ) : (
          <ReferableIcon referable={referable} notConfiguredCount={notConfiguredCount} />
        )}
      </td>
      {/* 左 spacer: 画面外（左側）の列幅を確保 */}
      <td aria-hidden="true" className="p-0" style={{ width: leftSpacerWidth }} />
      {columns.map((col, colIdx) => {
        const showGroupBorder =
          colIdx < columns.length - 1 && columns[colIdx + 1].data_classification_code !== col.data_classification_code
        const isRequired = requiredSet.has(col.column_code)
        // 必須 が優先。同一行で両方に含まれることは無い（setting.json 側で保証）
        const isOptional = !isRequired && optionalSet.has(col.column_code)
        const status = isRequired || isOptional ? statusByCode[col.column_code] || 'unset' : null
        return (
          <td
            key={`${row.graphId}-${col.column_code}`}
            className="p-0"
            style={{
              height: '28px',
              ...(showGroupBorder ? { borderRight: `1px solid ${COLUMN_GROUP_BORDER_COLOR}` } : {})
            }}
          >
            <CellDot required={isRequired} optional={isOptional} status={status} isHeader={false} />
          </td>
        )
      })}
      {/* 右 spacer: 画面外（右側）の列幅を確保 */}
      <td aria-hidden="true" className="p-0" style={{ width: rightSpacerWidth }} />
    </tr>
  )
}

MatrixRow.propTypes = {
  row: PropTypes.object.isRequired,
  majorSpan: PropTypes.number.isRequired,
  middleSpan: PropTypes.number.isRequired,
  dashboardSpan: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  statusByCode: PropTypes.object.isRequired,
  mappingLoading: PropTypes.bool,
  onGraphClick: PropTypes.func.isRequired,
  leftSpacerWidth: PropTypes.number,
  rightSpacerWidth: PropTypes.number
}

MatrixRow.defaultProps = {
  mappingLoading: false,
  leftSpacerWidth: 0,
  rightSpacerWidth: 0
}

export default React.memo(MatrixRow)
